/**
 * Contextual-query chips for the header search (T-148 P4b-1c).
 *
 * The typed text is parsed on every keystroke (parseQuery, query.v1). The user
 * can then edit what was understood: remove a chip, pick a range on the
 * calendar, choose another entity, or switch the date field. Those edits are
 * `FilterOverrides`, and each one records the typed value it replaced. When the
 * text changes so that value is no longer typed, the edit lapses and the new
 * parse shows through (pruneOverrides), so a chip never sticks to a phrase the
 * user has already deleted.
 *
 * Filters can also come from an AI reading (T-151, understandMerge.ts), which
 * may name any search.v1 entity and the date field it read, so the chip types
 * are wider than the grammar's: every search.v1 entity, and Sales' `event`.
 */
import { parseQuery, type DateField, type QueryEntity } from './parseQuery';

/** Every search.v1 entity a filter can name: the grammar's nouns, plus ones only an AI reading or a menu produces. */
export type SearchEntity =
  | QueryEntity | 'damage_finding' | 'document' | 'lead' | 'purchase_order' | 'vendor' | 'vendor_contact';

/** The grammar's date fields, plus Sales' `event` (a quote's event date). */
export type ChipDateField = DateField | 'event';

export interface ChipDate {
  /** null = each entity's default field (no entity chip). */
  field: ChipDateField | null;
  /** Local calendar dates, half-open: from ≤ day < to. */
  from: string;
  to: string;
  /** True when no date was typed and the grammar supplied one (R6). */
  implicit?: true;
}

/** The parser's QueryFilters, widened to what an AI reading can fill. */
export interface ChipFilters {
  entity?: SearchEntity;
  company_ids?: string[];
  date?: ChipDate;
  model_codes?: string[];
  status?: string[];
}

export const SEARCH_ENTITIES: readonly SearchEntity[] = [
  'unit', 'group', 'site', 'company', 'work_order', 'damage_finding', 'checkin', 'document',
  'contact', 'quote', 'rental', 'lead', 'purchase_order', 'vendor', 'vendor_contact',
];
export const CHIP_DATE_FIELDS: readonly ChipDateField[] = ['active', 'created', 'activity', 'occurred', 'event'];

export interface DateRange { from: string; to: string }

export interface FilterOverrides {
  /** Keys (chipKey) of typed filters the user removed. */
  removed: string[];
  /** A range picked on the calendar; `replaces` = the typed date's key, or null if none was typed. */
  date?: { value: DateRange; replaces: string | null };
  /** An entity chosen from the entity chip's menu, and the typed entity it replaced. */
  entity?: { value: SearchEntity; replaces: SearchEntity | null };
  /** The date-field toggle, for the entity it was chosen on. */
  dateField?: { value: ChipDateField; entity: SearchEntity };
}

export const EMPTY_OVERRIDES: FilterOverrides = Object.freeze({ removed: [] }) as FilterOverrides;

/** R5: an entity's date fields, its default first. Empty = no date filter in v1. */
const DATE_FIELDS: Partial<Record<SearchEntity, ChipDateField[]>> = {
  group: ['active', 'created'], site: ['active', 'created'], rental: ['active', 'created'],
  unit: ['activity'], checkin: ['occurred'],
  work_order: ['created'], contact: ['created'], quote: ['created', 'event'],
};

export function dateFieldChoices(entity: SearchEntity | undefined): ChipDateField[] {
  return entity ? DATE_FIELDS[entity] ?? [] : [];
}

const dateKey = (d: DateRange | undefined) => (d ? `date:${d.from}/${d.to}` : null);

export function hasFilters(f: ChipFilters): boolean {
  return Boolean(f.entity || f.company_ids?.length || f.date || f.model_codes?.length || f.status?.length);
}

/** Keys of everything the parse understood (what `removed` may refer to). */
function parsedKeys(p: ChipFilters): Set<string> {
  const keys = new Set<string>();
  if (p.entity) keys.add(`entity:${p.entity}`);
  p.company_ids?.forEach((id) => keys.add(`company:${id}`));
  p.model_codes?.forEach((m) => keys.add(`model:${m}`));
  p.status?.forEach((s) => keys.add(`status:${s}`));
  const d = dateKey(p.date);
  if (d) keys.add(d);
  return keys;
}

/** Drop edits whose typed value is gone. Returns `o` itself when nothing lapsed. */
export function pruneOverrides(o: FilterOverrides, parsed: ChipFilters): FilterOverrides {
  const keys = parsedKeys(parsed);
  const removed = o.removed.filter((k) => keys.has(k));
  const dateLive = !o.date || o.date.replaces === dateKey(parsed.date);
  const entityLive = !o.entity || o.entity.replaces === (parsed.entity ?? null);
  // The date-field toggle belongs to one date on one entity: it lapses once no
  // date is in play or the entity changed, so an old choice never resurfaces.
  const entityNow = entityLive && o.entity ? o.entity.value : parsed.entity;
  const dateInPlay = Boolean(parsed.date) || Boolean(dateLive && o.date);
  const fieldLive = !o.dateField || (dateInPlay && o.dateField.entity === entityNow);
  if (removed.length === o.removed.length && dateLive && entityLive && fieldLive) return o;
  return {
    ...o,
    removed,
    date: dateLive ? o.date : undefined,
    entity: entityLive ? o.entity : undefined,
    dateField: fieldLive ? o.dateField : undefined,
  };
}

/** What the search sends: the parse, with the user's chip edits applied. */
export function effectiveFilters(parsed: ChipFilters, raw: FilterOverrides): ChipFilters {
  const o = pruneOverrides(raw, parsed);
  const gone = new Set(o.removed);
  const out: ChipFilters = {};

  const entity = o.entity ? o.entity.value : parsed.entity;
  if (entity && (o.entity || !gone.has(`entity:${entity}`))) out.entity = entity;

  const companies = (parsed.company_ids ?? []).filter((id) => !gone.has(`company:${id}`));
  if (companies.length) out.company_ids = companies;
  const models = (parsed.model_codes ?? []).filter((m) => !gone.has(`model:${m}`));
  if (models.length) out.model_codes = models;
  const status = (parsed.status ?? []).filter((s) => !gone.has(`status:${s}`));
  if (status.length) out.status = status;

  let range: DateRange | undefined;
  let implicit = false;
  // The field the reading carries (an AI reading may name one; the parser's is
  // always the entity's default), honored below when the entity offers it. It
  // belongs to the entity it was read for: a chosen entity re-derives its default.
  let carried: ChipDateField | null = null;
  if (o.date) range = o.date.value;
  else if (parsed.date && !gone.has(dateKey(parsed.date)!)) {
    carried = o.entity ? null : parsed.date.field;
    range = { from: parsed.date.from, to: parsed.date.to };
    // R6: "active today" exists only because productions were filtered by model.
    implicit = Boolean(parsed.date.implicit);
    if (implicit && !(out.entity === 'group' && out.model_codes)) range = undefined;
  }
  if (range) {
    const choices = dateFieldChoices(out.entity);
    if (!out.entity || !choices.length) {
      // No entity: each entity uses its default field. An entity with no date
      // field (company) keeps the date too: the server then matches nothing,
      // which is honest. Dropping the date would silently widen the query (R4).
      out.date = { field: null, ...range };
    } else {
      const picked = o.dateField && o.dateField.entity === out.entity && choices.includes(o.dateField.value)
        ? o.dateField.value
        : carried && choices.includes(carried) ? carried : choices[0];
      out.date = { field: picked, ...range, ...(implicit ? { implicit: true as const } : {}) };
    }
  }
  return out;
}

// ------------------------------------------------------------------ edits

export type ChipKind = 'entity' | 'company' | 'model' | 'status' | 'date';
export interface Chip {
  key: string;
  kind: ChipKind;
  label: string;
  /** Filled by an AI reading (T-151), not by what the parser understood. */
  ai?: true;
}

/** Remove a chip: a typed filter is suppressed; a picked range or chosen entity is undone. */
export function removeChip(o: FilterOverrides, chip: Chip, parsed: ChipFilters): FilterOverrides {
  const next = pruneOverrides(o, parsed);
  if (chip.kind === 'date' && next.date) {
    const { replaces } = next.date;
    return { ...next, date: undefined, removed: replaces ? [...next.removed, replaces] : next.removed };
  }
  if (chip.kind === 'entity' && next.entity) {
    const { replaces } = next.entity;
    return { ...next, entity: undefined, removed: replaces ? [...next.removed, `entity:${replaces}`] : next.removed };
  }
  return { ...next, removed: [...next.removed, chip.key] };
}

export function pickDate(o: FilterOverrides, value: DateRange, parsed: ChipFilters): FilterOverrides {
  const next = pruneOverrides(o, parsed);
  return { ...next, date: { value, replaces: dateKey(parsed.date) } };
}

export function chooseEntity(o: FilterOverrides, value: SearchEntity, parsed: ChipFilters): FilterOverrides {
  const next = pruneOverrides(o, parsed);
  return { ...next, entity: { value, replaces: parsed.entity ?? null } };
}

export function chooseDateField(o: FilterOverrides, value: ChipDateField, entity: SearchEntity): FilterOverrides {
  return { ...o, dateField: { value, entity } };
}

// ------------------------------------------------------------------ labels

export const ENTITY_CHIP_LABEL: Record<SearchEntity, string> = {
  unit: 'Units', group: 'Productions', site: 'Sites', company: 'Companies', work_order: 'Work orders',
  damage_finding: 'Damage', checkin: 'Check-ins', document: 'Documents', contact: 'Contacts', quote: 'Quotes',
  rental: 'Rentals', lead: 'Leads', purchase_order: 'Purchase orders', vendor: 'Vendors', vendor_contact: 'Vendor contacts',
};

const FIELD_LABEL: Record<ChipDateField, string> = {
  active: 'Active', created: 'Created', activity: 'Activity', occurred: 'Occurred', event: 'Event',
};

function parts(iso: string) {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d, t: Date.UTC(y, m - 1, d) };
}
const shift = (iso: string, days: number) => new Date(parts(iso).t + days * 86_400_000).toISOString().slice(0, 10);
const fmt = (iso: string, withYear: boolean) => new Date(parts(iso).t).toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
});

/** The user's local date today, YYYY-MM-DD. */
export function localToday(now: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** "Created 2025", "Active Aug 2026", "Active today", "Active Sep 21 – Sep 27". */
export function dateLabel(date: ChipDate, now: Date, timeZone: string): string {
  const prefix = date.field ? `${FIELD_LABEL[date.field]} ` : '';
  const a = parts(date.from);
  const b = parts(date.to);
  const last = shift(date.to, -1);
  const thisYear = Number(localToday(now, timeZone).slice(0, 4));
  let body: string;
  if (a.m === 1 && a.d === 1 && b.m === 1 && b.d === 1 && b.y === a.y + 1) body = String(a.y);
  else if (a.d === 1 && b.d === 1 && (b.y * 12 + b.m) - (a.y * 12 + a.m) === 1) {
    body = new Date(a.t).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  } else if (last === date.from) {
    body = date.from === localToday(now, timeZone) ? 'today' : fmt(date.from, a.y !== thisYear);
  } else {
    const years = a.y !== parts(last).y || a.y !== thisYear;
    body = `${fmt(date.from, years)} – ${fmt(last, years)}`;
  }
  return `${prefix}${body}`;
}

export interface ChipLookups {
  companies: readonly { id: string; name: string }[];
  models: readonly { code: string; rooms?: number; label?: string }[];
  now: Date;
  timeZone: string;
}

function modelLabel(code: string, models: ChipLookups['models']): string {
  const m = models.find((x) => x.code === code);
  if (m?.rooms) return `${m.rooms} Room (${code})`;
  return m?.label ? `${m.label} (${code})` : code;
}

/**
 * The chips for effective filters, in reading order; the date chip last, beside
 * the calendar. A chip whose key is in `aiKeys` was filled by an AI reading.
 */
export function buildChips(f: ChipFilters, l: ChipLookups, aiKeys: readonly string[] = []): Chip[] {
  const chips: Chip[] = [];
  if (f.entity) chips.push({ key: `entity:${f.entity}`, kind: 'entity', label: ENTITY_CHIP_LABEL[f.entity] });
  f.company_ids?.forEach((id) => chips.push({
    key: `company:${id}`, kind: 'company', label: l.companies.find((c) => c.id === id)?.name ?? 'Company',
  }));
  f.model_codes?.forEach((m) => chips.push({ key: `model:${m}`, kind: 'model', label: modelLabel(m, l.models) }));
  f.status?.forEach((s) => chips.push({ key: `status:${s}`, kind: 'status', label: s[0].toUpperCase() + s.slice(1) }));
  if (f.date) chips.push({ key: dateKey(f.date)!, kind: 'date', label: dateLabel(f.date, l.now, l.timeZone) });
  if (!aiKeys.length) return chips;
  const fromAi = new Set(aiKeys);
  return chips.map((c) => (fromAi.has(c.key) ? { ...c, ai: true as const } : c));
}

// ------------------------------------------------------------------ transport

/** search.v1 filter params (R2), in a stable order. Dates are local to `timeZone`. */
export function toSearchParams(f: ChipFilters, timeZone: string): [string, string][] {
  const p: [string, string][] = [];
  if (f.entity) p.push(['entity', f.entity]);
  f.company_ids?.forEach((id) => p.push(['company_id', id]));
  if (f.date) {
    p.push(['date_from', f.date.from], ['date_to', f.date.to], ['tz', timeZone]);
    if (f.date.field) p.push(['date_field', f.date.field]);
  }
  f.model_codes?.forEach((m) => p.push(['model', m]));
  f.status?.forEach((s) => p.push(['status', s]));
  return p;
}

/** A calendar preset resolved by the grammar itself, so the picker and typing agree. */
export function presetRange(phrase: string, now: Date, timeZone: string): DateRange | null {
  const d = parseQuery(phrase, { now, timeZone, companies: [], models: [] }).filters.date;
  return d ? { from: d.from, to: d.to } : null;
}

export const DATE_PRESETS: readonly (readonly [label: string, phrase: string])[] = [
  ['Today', 'today'], ['This week', 'this week'], ['Last week', 'last week'], ['This month', 'this month'],
  ['Last month', 'last month'], ['This year', 'this year'], ['Last year', 'last year'],
];
